import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Select } from '@/components/ui/Select';

const PAGE_SIZES = [25, 50, 100, 200] as const;

/**
 * Compact pager for the list pages. Also hosts the rows-per-page select,
 * which used to sit in the filter grid at the top — the footer row has
 * free horizontal space, so it costs no extra height here.
 *
 * `page` / `limit` must come from the caller's own state, not from the
 * server's echoed pagination: the list hooks keep the previous response
 * as placeholder data while refetching, so server-echoed values lag a
 * click behind and swallow rapid paging.
 */
export function TablePagination({
  page,
  limit,
  total,
  totalPages,
  onPageChange,
  onLimitChange,
  rowsLabel,
  prevLabel,
  nextLabel,
  className,
}: {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  onPageChange: (next: number) => void;
  onLimitChange: (next: number) => void;
  rowsLabel: string;
  prevLabel: string;
  nextLabel: string;
  className?: string;
}) {
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const lastPage = Math.max(1, totalPages);
  // Keep an off-list page size (URL param, older saved state) selectable
  // instead of silently showing a value the state doesn't hold.
  const sizes = PAGE_SIZES.includes(limit as (typeof PAGE_SIZES)[number])
    ? [...PAGE_SIZES]
    : [...PAGE_SIZES, limit].sort((a, b) => a - b);

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 text-xs text-slate-500 dark:text-slate-400',
        className,
      )}
    >
      <span className="min-w-0 truncate tabular-nums">
        {from}–{to} / {total}
      </span>
      {/* The controls keep their size on narrow viewports; the range
          readout on the left is what gives way. */}
      <div className="flex flex-shrink-0 items-center gap-2">
        {/* Width lives on the wrapper: `Select` carries `w-full` in its base
            classes and `cn()` has no tailwind-merge, so a width passed via
            className would depend on stylesheet order to win. */}
        <div className="w-[4.5rem]">
          <Select
            uiSize="sm"
            aria-label={rowsLabel}
            title={rowsLabel}
            value={String(limit)}
            onChange={(e) => onLimitChange(Number(e.target.value))}
          >
            {sizes.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </div>
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-40 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          aria-label={prevLabel}
          title={prevLabel}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="whitespace-nowrap tabular-nums">
          {page} / {lastPage}
        </span>
        <button
          type="button"
          disabled={page >= lastPage}
          onClick={() => onPageChange(page + 1)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-40 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          aria-label={nextLabel}
          title={nextLabel}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
